import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SupportTicket } from '@/hooks/useSupportChat';
import { cn } from '@/lib/utils';
import { statusBadgeClasses, statusLabelKey } from './ticketStatus';

interface Props {
  tickets: SupportTicket[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onNew?: () => void;
  unreadSide: 'user' | 'admin';
}

export const TicketList = ({ tickets, activeId, onSelect, onNew, unreadSide }: Props) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col h-full bg-white">
      {onNew && (
        <div className="p-3 border-b border-[#DBE2EA]">
          <Button
            onClick={onNew}
            className="w-full bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] rounded-lg"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('supportChat.newTicket')}
          </Button>
        </div>
      )}
      <div className="flex-1 overflow-y-auto">
        {tickets.length === 0 ? (
          <div className="p-6 text-center text-sm text-[#5A6B7A]">
            {t('supportChat.empty')}
          </div>
        ) : (
          tickets.map((tk) => {
            const unread = unreadSide === 'user' ? tk.unread_for_user : tk.unread_for_admin;
            const isActive = tk.id === activeId;
            return (
              <button
                key={tk.id}
                onClick={() => onSelect(tk.id)}
                className={cn(
                  'w-full text-left px-4 py-3 border-b border-[#DBE2EA] hover:bg-[#EBF4F8] transition-colors',
                  isActive && 'bg-[#EBF4F8]'
                )}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p
                    className={cn(
                      'text-sm truncate text-[#2D3748]',
                      unread ? 'font-semibold' : 'font-normal'
                    )}
                  >
                    {tk.subject || t('supportChat.newTicket')}
                  </p>
                  {unread && (
                    <span className="w-2 h-2 rounded-full bg-[#FF7171] flex-shrink-0 mt-1.5" />
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={cn('text-[10px]', statusBadgeClasses(tk.status))}>
                    {t(statusLabelKey(tk.status))}
                  </Badge>
                  <span className="text-[10px] text-[#87ADD4]">
                    {new Date(tk.last_message_at).toLocaleDateString('fr-FR', {
                      day: '2-digit',
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};
